import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { PortalPageHeader } from "@/components/portal-shell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Trash2, History, Play } from "lucide-react";
import {
  listPortalSubscriptions,
  togglePortalSubscription,
  deletePortalSubscription,
  runPortalSubscriptionNow,
} from "@/lib/portal-reports/subscriptions.functions";
import { runServerFn } from "@/lib/server-fn";
import { getReport } from "@/lib/reporting/framework/registry";

export const Route = createFileRoute("/_authenticated/portal/reports/subscriptions")({ component: SubscriptionsPage });

function SubscriptionsPage() {
  const listFn = useServerFn(listPortalSubscriptions);
  const toggleFn = useServerFn(togglePortalSubscription);
  const deleteFn = useServerFn(deletePortalSubscription);
  const runNowFn = useServerFn(runPortalSubscriptionNow);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["portal-subs-all"],
    queryFn: () => runServerFn(() => listFn({ data: {} }), "Loading subscriptions"),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["portal-subs-all"] });

  const toggle = useMutation({
    mutationFn: (vars: { id: string; active: boolean }) =>
      runServerFn(() => toggleFn({ data: vars }), "Updating"),
    onSuccess: () => invalidate(),
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  const del = useMutation({
    mutationFn: (id: string) => runServerFn(() => deleteFn({ data: { id } }), "Deleting"),
    onSuccess: () => { toast.success("Removed"); invalidate(); },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  const runNow = useMutation({
    mutationFn: (id: string) => runServerFn(() => runNowFn({ data: { id } }), "Queueing"),
    onSuccess: () => { toast.success("Queued — will run within 15 minutes"); invalidate(); },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  return (
    <>
      <PortalPageHeader
        title="Report Subscriptions"
        description="Recurring reports delivered to your team."
        actions={
          <div className="flex gap-2">
            <Link to="/portal/reports/history"><Button variant="outline" size="sm"><History className="h-4 w-4 mr-1" /> History</Button></Link>
            <Link to="/portal/reports"><Button variant="ghost" size="sm"><ArrowLeft className="h-4 w-4 mr-1" /> Reports</Button></Link>
          </div>
        }
      />
      <div className="p-4 sm:p-6">
        <Card>
          <CardContent className="p-0">
            {isLoading && <div className="p-6 text-sm text-muted-foreground">Loading…</div>}
            {!isLoading && (data ?? []).length === 0 && (
              <div className="p-6 text-sm text-muted-foreground">No subscriptions yet. Open any report and click <b>Subscribe</b> to schedule it.</div>
            )}
            {(data ?? []).length > 0 && (
              <div className="divide-y">
                {(data ?? []).map((s: any) => {
                  const def = getReport(s.report_key);
                  return (
                    <div key={s.id} className="flex items-center gap-3 p-3 sm:p-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          {def ? (
                            <Link to="/portal/reports/$reportKey" params={{ reportKey: s.report_key }} className="font-medium text-sm hover:underline">
                              {def.title}
                            </Link>
                          ) : (
                            <span className="font-medium text-sm">{s.report_key}</span>
                          )}
                          <Badge variant="secondary" className="text-[10px]">{s.cadence}</Badge>
                          <Badge variant="outline" className="text-[10px] uppercase">{s.format}</Badge>
                          {!s.active && <Badge variant="outline" className="text-[10px]">paused</Badge>}
                        </div>
                        <div className="text-xs text-muted-foreground truncate mt-0.5">{(s.recipients ?? []).join(", ")}</div>
                        <div className="text-[11px] text-muted-foreground/80 mt-0.5">
                          Next: {s.next_run_at ? new Date(s.next_run_at).toLocaleString() : "—"}
                          {s.last_run_at && (
                            <>
                              {" "}· Last: {new Date(s.last_run_at).toLocaleString()}{" "}
                              {s.last_status && (
                                <Badge
                                  variant={s.last_status === "succeeded" ? "secondary" : s.last_status === "failed" ? "destructive" : "outline"}
                                  className="text-[10px] ml-1 align-middle"
                                >
                                  {s.last_status}
                                </Badge>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        title="Run now"
                        disabled={runNow.isPending}
                        onClick={() => runNow.mutate(s.id)}
                      >
                        <Play className="h-4 w-4" />
                      </Button>
                      <Switch checked={s.active} onCheckedChange={(v) => toggle.mutate({ id: s.id, active: v })} />
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => del.mutate(s.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}